import React, { useState, useEffect } from 'react';
import Header from '../adminComponents/header/Header';
import { Box, Button, Grid, Paper } from '@mui/material';
import { useFormik } from 'formik';
import ImageUploadField from '../adminComponents/CommonTextField/ImageUploadField';
import CustomTextField from '../adminComponents/CommonTextField/CustomTextField';
import * as Yup from 'yup';
import SuccessModal from '../adminComponents/successModal/SuccessModal';
import VideoUploadField from '../adminComponents/CommonTextField/VideoUploadField';
import { getAboutUs, udpateAboutUs } from '../services/AboutUs';
import { useMediaQueries } from '../usecontext/UseProvider';
import { useNavigate } from 'react-router-dom';

// Modified validation schema to not require files if they already exist
const validationSchema = Yup.object({
    image: Yup.mixed().test(
        'fileOrExisting',
        'Image is required',
        (value) => {
            // If it's a string (URL), it means we have an existing image
            // If it's a File object, it's a new upload
            // Both are valid
            return typeof value === 'string' || value instanceof File;
        }
    ),
    title: Yup.string().required('The title field is required.'),
    description: Yup.string().required('The description field is required.'),
    yearsOfExperience: Yup.string().required('The your experience field is required.'),
    tags: Yup.string().required('The highlights field is required.'),
    satisfiedClients: Yup.string().required('Satisfied Customer count is required'),
    zulaItems: Yup.string().required('The zula items field is required.'),
    video: Yup.mixed().test(
        'fileOrExisting',
        'Video is required',
        (value) => {
            return typeof value === 'string' || value instanceof File;
        }
    ),
});

const EditAboutUS = () => {
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [categoryData, setCategoryData] = useState([]);
    const [aboutusId, setaboutusId] = useState();
    const { setLoading } = useMediaQueries();
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            image: null,
            title: '',
            description: '',
            yearsOfExperience: '',
            tags: '',
            satisfiedClients: '',
            zulaItems: '',
            video: null,
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                // Create FormData for file upload
                const formData = new FormData();

                // Append all fields to formData
                Object.keys(values).forEach(key => {
                    if (key === 'image' || key === 'video') {
                        // Only append file if it's a new File object
                        if (values[key] instanceof File) {
                            formData.append(key, values[key]);
                        }
                    } else {
                        formData.append(key, values[key]);
                    }
                });

                const response = await udpateAboutUs(formData, aboutusId);
                if (response?.error) {
                    console.error(response.error);
                } else {
                    setSuccessModalOpen(true);
                    navigate('/admin/aboutus');
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
    };

    const fetchAboutUsData = async () => {
        try {
            setLoading(true);
            const response = await getAboutUs();
            if (response?.aboutUs) {
                const {
                    image,
                    title,
                    description,
                    yearsOfExperience,
                    tags,
                    satisfiedClients,
                    zulaItems,
                    video,
                    _id
                } = response.aboutUs;

                setaboutusId(_id);
                formik.setValues({
                    image: image?.url || null, // Store the existing image URL
                    title,
                    description,
                    yearsOfExperience,
                    tags: tags?.join(', '),
                    satisfiedClients,
                    zulaItems,
                    video: video?.url || null // Store the existing video URL
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAboutUsData();
    }, []);

    return (
        <>
            <Header title="Edit Aboutus" />
            <Box sx={{ pt: 3 }}>
                <Paper sx={{ p: { sm: 5, xs: 3 } }}>
                    <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <ImageUploadField
                                    label="Image"
                                    name="image"
                                    formik={formik}
                                    data={categoryData}
                                    imageHeight='70px'
                                    imageWidth='70px'
                                    existingImageUrl={typeof formik.values.image === 'string' ? formik.values.image : null}
                                />
                                <CustomTextField
                                    label="Title"
                                    name="title"
                                    formik={formik}
                                    placeholder="Enter title"
                                    size='small'
                                    sx={{ mt: 2 }}
                                />
                                <CustomTextField
                                    label="Description"
                                    name="description"
                                    formik={formik}
                                    placeholder="Enter Description"
                                    minRows={4}
                                    multiline={true}
                                />
                                <CustomTextField
                                    label="Your Experience"
                                    name="yearsOfExperience"
                                    formik={formik}
                                    placeholder="Enter your experience"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <VideoUploadField
                                    label="Upload Video"
                                    name="video"
                                    formik={formik}
                                    existingVideoUrl={typeof formik.values.video === 'string' ? formik.values.video : null}
                                />
                                <CustomTextField
                                    label="Highlights (comma separated)"
                                    name="tags"
                                    formik={formik}
                                    placeholder="Enter highlights separated by commas"
                                    minRows={4}
                                    multiline={true}
                                />
                                <CustomTextField
                                    label="Satisfied Customer"
                                    name="satisfiedClients"
                                    formik={formik}
                                    placeholder="Enter Satisfied Customer"
                                />
                                <CustomTextField
                                    label="Zula items"
                                    name="zulaItems"
                                    formik={formik}
                                    placeholder="Enter zula items"
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 4 }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    borderRadius: '10px',
                                    borderColor: '#01A0E2',
                                    color: '#000',
                                    width: '150px',
                                    textTransform: 'none'
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    borderRadius: '10px',
                                    backgroundColor: '#01A0E2',
                                    color: '#fff',
                                    width: '150px',
                                    textTransform: 'none'
                                }}
                            >
                                Update
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Box>
            <SuccessModal
                open={successModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                message={'About us updated successfully'}
            />
        </>
    )
}

export default EditAboutUS;